import { Card, CardActionArea, CardMedia, CardContent, Typography, Box } from '@mui/material';
import {
  resolvePhoneImage,
  BRAND_DEFAULT_PHONE_IMAGE
} from './AddDeviceDialog/utils/phoneImageResolver';
// 레거시 컴포넌트용 타입 정의 (통합 데이터와 독립적)
interface DeviceModel {
  name: string;
  image: string;
  recommended?: boolean;
  subcategory?: string;
}

// 기본 이미지 경로 - 각 기기군별 폴백 이미지 (스마트폰은 phoneImageResolver 담당)
const DEFAULT_IMAGES = {
  iPad: '/assets/ipad/iPadDefault.png',
  Watch: '/assets/watchDefault.png',
  AirPods: '/assets/airPot/에어팟3세대.png',
};
import { useState } from 'react';

interface Props {
  model: DeviceModel;
  selected: boolean;
  onClick: () => void;
}

export default function DeviceModelCard({ model, selected, onClick }: Props) {
  const [imageError, setImageError] = useState(false);

  // 🎯 이미지 로드 실패 시 폴백 — 스마트폰·태블릿은 매니페스트 리졸버(실존 파일 보장)로 위임
  const getDefaultImage = () => {
    const name = model.name || '';
    if (/탭|Tab/i.test(name) && (name.includes('갤럭시') || /Galaxy/i.test(name))) {
      return resolvePhoneImage({ brand: 'Samsung', modelKo: name });
    }
    if (model.subcategory?.includes('iPad') || name.includes('아이패드') || name.includes('iPad')) {
      return resolvePhoneImage({ brand: 'Apple', modelKo: name });
    }
    if (name.includes('아이폰') || name.includes('iPhone')) {
      return resolvePhoneImage({ brand: 'Apple', modelKo: name });
    }
    if (name.includes('갤럭시') || /Galaxy/i.test(name)) {
      // 🔥 버그 수정: 기존에는 삼성 분기가 없어 갤럭시 폰에 iPad 기본 이미지가 표시됐음
      return resolvePhoneImage({ brand: 'Samsung', modelKo: name });
    }
    if (name.includes('워치')) {
      return DEFAULT_IMAGES.Watch;
    }
    if (name.includes('AirPods') || name.includes('에어팟')) {
      return DEFAULT_IMAGES.AirPods;
    }
    return BRAND_DEFAULT_PHONE_IMAGE;
  };

  const handleImageError = () => {
    // 🔍 iPhone 시리즈 이미지 로드 실패 디버깅
    if (model.name.includes('iPhone') || model.name.includes('아이폰')) {
      console.error(`❌ iPhone 이미지 로드 실패: ${model.name}`);
      console.error(`  - 시도한 이미지 경로: ${model.image}`);
      console.error(`  - 폴백 이미지로 전환: ${getDefaultImage()}`);
    }
    
    // 이미지 로드 실패 - fallback 이미지 사용
    setImageError(true);
  };
  
  const handleImageLoad = () => {
    // 🔍 iPhone 시리즈 이미지 로드 성공 디버깅
    if (model.name.includes('iPhone') || model.name.includes('아이폰')) {
      console.log(`✅ iPhone 이미지 로드 성공: ${model.name} → ${model.image}`);
    }
  };

  return (
    <Card
      sx={{
        border: selected ? '2.5px solid #6366f1' : '1.5px solid #e0e7ef',
        borderRadius: 4,
        boxShadow: selected ? '0 4px 24px rgba(99,102,241,0.13)' : '0 2px 8px rgba(0,0,0,0.04)',
        transition: 'all 0.18s',
        outline: selected ? '2px solid #6366f1' : 'none',
        position: 'relative',
        cursor: 'pointer',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: selected
            ? 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)'
            : 'transparent',
          transition: 'all 0.3s ease',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: selected
            ? '0 20px 40px rgba(99, 102, 241, 0.15), 0 8px 16px rgba(99, 102, 241, 0.1)'
            : '0 20px 40px rgba(0,0,0,0.08), 0 8px 16px rgba(0,0,0,0.04)',
          background: selected ? 'rgba(99, 102, 241, 0.08)' : 'rgba(255, 255, 255, 1)',
          borderColor: selected ? '#6366f1' : 'rgba(99, 102, 241, 0.2)',
          outline: selected ? '2px solid #6366f1' : '2px solid rgba(99, 102, 241, 0.35)',
        },
        '&:hover::before': {
          background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
        },
      }}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick()}
    >
      <CardActionArea>
        <CardMedia
          component="img"
          height="100"
          image={imageError ? getDefaultImage() : model.image}
          alt={model.name}
          onError={handleImageError}
          onLoad={handleImageLoad}
          sx={{ objectFit: 'contain', p: 2, background: '#f8fafc' }}
        />
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} align="center">
            {model.name}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
} 